import EditCalendarIcon from "@mui/icons-material/EditCalendar";
import TodayIcon from "@mui/icons-material/Today";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import {
  Box,
  Button,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha, type SxProps, type Theme } from "@mui/material/styles";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay, type PickersDayProps } from "@mui/x-date-pickers/PickersDay";
import React from "react";
import { useTranslation } from "react-i18next";

import { FilterMenuButton, type FilterMenuOption } from "@/components/common/FilterMenuButton";
import DeviceTypeFilter, { type DeviceType } from "@/components/filters/DeviceTypeFilter";
import { getDateFnsLocale } from "@/i18n/dateFnsLocale";
import {
  formatStoreDateKey,
  formatStoreDateRangeForComparison,
  getBrowserTimezone,
  getEndOfStoreDay,
  getStartOfStoreDay,
  shouldShowStoreDateRangeComparisonYears,
} from "@/utils/storeTime";

export type DatePeriod =
  "yesterday" | "today" | "week_to_date" | "last_weekend" | "last_4_weeks" | "custom";

interface DateRange {
  start: Date;
  end: Date; // exclusive end in existing usage
}

export type TrendPeriod = "day_before" | "week_before" | "month_before" | "year_before";

const mobileFilterControlHeight = 40;

const CustomCalendarIcon = ({ name }: { name: string }) => (
  <Box
    component="span"
    className="MuiSvgIcon-root"
    sx={{
      display: "inline-block",
      width: "1em",
      height: "1em",
      backgroundColor: "currentColor",
      mask: `url(/images/icons/mui-custom/${name}.svg) center / contain no-repeat`,
      WebkitMask: `url(/images/icons/mui-custom/${name}.svg) center / contain no-repeat`,
    }}
  />
);

interface ReportingWindowSelectorProps {
  value: DatePeriod;
  onChange: (value: DatePeriod) => void;
  availablePeriods?: readonly DatePeriod[];
  showRangeText?: boolean;
  dateRange?: DateRange; // when provided and showRangeText, renders the formatted range below
  enableCustom?: boolean; // opt-in/out of custom option
  customRange?: DateRange; // controlled custom range
  onCustomRangeChange?: (range: DateRange) => void; // notify parent when custom range applied
  className?: string;
  showDeviceFilter?: boolean;
  selectedDeviceType?: DeviceType;
  onDeviceTypeChange?: (deviceType: DeviceType) => void;
  includeAllDeviceOption?: boolean;
  deviceFilterDisabled?: boolean;
  areaFilter?: React.ReactNode;
  showTrendSelector?: boolean;
  trendPeriod?: TrendPeriod;
  onTrendPeriodChange?: (trendPeriod: TrendPeriod) => void;
  trendComparisonRange?: DateRange;
  anchorTrendComparisonToWeekdays?: boolean;
  onAnchorTrendComparisonToWeekdaysChange?: (anchored: boolean) => void;
}

const capitalizeFirstCharacter = (value: string) => {
  if (!value) {
    return value;
  }

  return value.charAt(0).toLocaleUpperCase() + value.slice(1);
};

// Reusable reporting window selector used across dashboards to select reporting windows.

const ReportingWindowSelector: React.FC<ReportingWindowSelectorProps> = ({
  value,
  onChange,
  availablePeriods,
  showRangeText = true,
  dateRange,
  enableCustom = true,
  customRange,
  onCustomRangeChange,
  className,
  showDeviceFilter = false,
  selectedDeviceType = "all",
  onDeviceTypeChange,
  includeAllDeviceOption = true,
  deviceFilterDisabled = false,
  areaFilter,
  showTrendSelector = false,
  trendPeriod = "week_before",
  onTrendPeriodChange,
  trendComparisonRange,
  anchorTrendComparisonToWeekdays = true,
  onAnchorTrendComparisonToWeekdaysChange,
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const periodControlFontSize = React.useMemo(
    () => ({
      xs: theme.typography.pxToRem(13),
      md: theme.typography.pxToRem(13),
    }),
    [theme],
  );
  const responsiveFilterControlHeight = mobileFilterControlHeight;
  const customDateFieldSx: SxProps<Theme> = {
    width: "100%",
    "& .MuiInputBase-root": {
      height: responsiveFilterControlHeight,
      minHeight: responsiveFilterControlHeight,
      maxHeight: responsiveFilterControlHeight,
    },
    "& .MuiInputBase-input": {
      fontSize: periodControlFontSize,
      fontWeight: 700,
    },
  };
  const customApplyButtonSx: SxProps<Theme> = {
    height: responsiveFilterControlHeight,
    minHeight: responsiveFilterControlHeight,
    maxHeight: responsiveFilterControlHeight,
  };
  const dateLocale = React.useMemo(
    () => getDateFnsLocale(i18n.resolvedLanguage || i18n.language),
    [i18n.resolvedLanguage, i18n.language],
  );
  const browserTimezone = React.useMemo(() => getBrowserTimezone(), []);
  const periodOptions = React.useMemo<FilterMenuOption<DatePeriod>[]>(() => {
    const options: FilterMenuOption<DatePeriod>[] = [
      {
        value: "today",
        label: t("filters.periods.today"),
        icon: <CustomCalendarIcon name="calendar_day" />,
      },
      {
        value: "yesterday",
        label: t("filters.periods.yesterday"),
        icon: <CustomCalendarIcon name="calendar_yesterday" />,
      },
      {
        value: "week_to_date",
        label: t("common.datePeriodToggle.text.weekToDate"),
        icon: <CustomCalendarIcon name="calendar_week" />,
      },
      {
        value: "last_weekend",
        label: t("common.datePeriodToggle.text.lastWeekend"),
        icon: <CustomCalendarIcon name="calendar_weekend" />,
      },
      {
        value: "last_4_weeks",
        label: t("common.datePeriodToggle.text.last4Weeks"),
        icon: <CustomCalendarIcon name="calendar_month" />,
      },
    ];

    if (enableCustom) {
      options.push({
        value: "custom",
        label: t("filters.custom"),
        icon: <EditCalendarIcon />,
      });
    }

    const visibleOptions = availablePeriods
      ? options.filter((option) => availablePeriods.includes(option.value))
      : options;

    return visibleOptions.map((option, index) => ({
      ...option,
      showDividerAfter: visibleOptions[index + 1]?.value === "custom",
    }));
  }, [availablePeriods, enableCustom, t]);
  const isDayBeforeAvailable =
    value === "today" ||
    value === "yesterday" ||
    (value === "custom" &&
      dateRange !== undefined &&
      formatStoreDateKey(dateRange.start, browserTimezone) ===
        formatStoreDateKey(dateRange.end, browserTimezone));
  const trendOptions = React.useMemo<FilterMenuOption<TrendPeriod>[]>(() => {
    const options: FilterMenuOption<TrendPeriod>[] = [
      {
        value: "week_before",
        label: t("common.datePeriodToggle.text.weekBefore"),
        icon: <CustomCalendarIcon name="calendar_week" />,
      },
      {
        value: "month_before",
        label: t("common.datePeriodToggle.text.monthBefore"),
        icon: <CustomCalendarIcon name="calendar_month" />,
      },
      {
        value: "year_before",
        label: t("common.datePeriodToggle.text.yearBefore"),
        icon: <CustomCalendarIcon name="calendar_year" />,
      },
    ];

    if (isDayBeforeAvailable) {
      options.unshift({
        value: "day_before",
        label: t("common.datePeriodToggle.text.dayBefore"),
        icon: <CustomCalendarIcon name="calendar_yesterday" />,
      });
    }

    return options;
  }, [isDayBeforeAvailable, t]);
  const [start, setStart] = React.useState<Date>(() => {
    return getStartOfStoreDay(customRange?.start ?? new Date(), browserTimezone);
  });
  const [end, setEnd] = React.useState<Date>(() => {
    return getEndOfStoreDay(customRange?.end ?? new Date(), browserTimezone);
  });

  const RangePreviewDay = React.useMemo(() => {
    const startDateKey = formatStoreDateKey(start, browserTimezone);
    const endDateKey = formatStoreDateKey(end, browserTimezone);
    const hasValidRange = startDateKey <= endDateKey;

    return function RangePreviewDay(props: PickersDayProps<Date>) {
      const dayDateKey = formatStoreDateKey(props.day, browserTimezone);
      const isRangeStart = hasValidRange && dayDateKey === startDateKey;
      const isRangeEnd = hasValidRange && dayDateKey === endDateKey;
      const isSingleDayRange = isRangeStart && isRangeEnd;
      const isWithinRange = hasValidRange && dayDateKey >= startDateKey && dayDateKey <= endDateKey;

      return (
        <PickersDay
          {...props}
          sx={(pickerTheme) => {
            const rangeFill = pickerTheme.palette.primary.main;
            const unselectedHoverFill = alpha(pickerTheme.palette.primary.main, 0.16);
            const isRtl = pickerTheme.direction === "rtl";
            const rangeBridge = [
              !isRangeStart && `-2px 0 0 ${rangeFill}`,
              !isRangeEnd && `2px 0 0 ${rangeFill}`,
            ]
              .filter(Boolean)
              .join(", ");

            return {
              ...(isWithinRange && {
                backgroundColor: rangeFill,
                borderRadius: isSingleDayRange ? "50%" : 0,
                boxShadow: rangeBridge,
                color: pickerTheme.palette.primary.contrastText,
              }),
              ...(isRangeStart &&
                !isSingleDayRange && {
                  borderTopLeftRadius: isRtl ? 0 : "50%",
                  borderBottomLeftRadius: isRtl ? 0 : "50%",
                  borderTopRightRadius: isRtl ? "50%" : 0,
                  borderBottomRightRadius: isRtl ? "50%" : 0,
                }),
              ...(isRangeEnd &&
                !isSingleDayRange && {
                  borderTopLeftRadius: isRtl ? "50%" : 0,
                  borderBottomLeftRadius: isRtl ? "50%" : 0,
                  borderTopRightRadius: isRtl ? 0 : "50%",
                  borderBottomRightRadius: isRtl ? 0 : "50%",
                }),
              ...(props.today &&
                !isWithinRange && {
                  backgroundColor: pickerTheme.palette.action.selected,
                  border: 0,
                  borderRadius: "50%",
                }),
              "&.MuiPickersDay-today": {
                border: 0,
                ...(isWithinRange && {
                  backgroundColor: rangeFill,
                  color: pickerTheme.palette.primary.contrastText,
                }),
              },
              ...(isWithinRange && {
                "&.MuiPickersDay-today:not(.Mui-selected)": {
                  backgroundColor: rangeFill,
                  color: pickerTheme.palette.primary.contrastText,
                },
              }),
              ...(isWithinRange && {
                "&.Mui-selected": {
                  backgroundColor: rangeFill,
                  color: pickerTheme.palette.primary.contrastText,
                },
              }),
              "@media (pointer: fine)": {
                "&:hover": {
                  backgroundColor: isWithinRange
                    ? pickerTheme.palette.primary.dark
                    : unselectedHoverFill,
                  color: isWithinRange
                    ? pickerTheme.palette.primary.contrastText
                    : pickerTheme.palette.text.primary,
                },
                ...(isWithinRange && {
                  "&.MuiPickersDay-today:not(.Mui-selected):hover": {
                    backgroundColor: pickerTheme.palette.primary.dark,
                    color: pickerTheme.palette.primary.contrastText,
                  },
                }),
              },
              "&:focus": {
                backgroundColor: isWithinRange
                  ? pickerTheme.palette.primary.dark
                  : unselectedHoverFill,
                color: isWithinRange
                  ? pickerTheme.palette.primary.contrastText
                  : pickerTheme.palette.text.primary,
              },
              ...(isWithinRange && {
                "&.MuiPickersDay-today:not(.Mui-selected):focus": {
                  backgroundColor: pickerTheme.palette.primary.dark,
                  color: pickerTheme.palette.primary.contrastText,
                },
              }),
            };
          }}
        />
      );
    };
  }, [browserTimezone, end, start]);

  // Keep internal state in sync when parent controls customRange
  React.useEffect(() => {
    if (customRange) {
      setStart(getStartOfStoreDay(customRange.start, browserTimezone));
      setEnd(getEndOfStoreDay(customRange.end, browserTimezone));
    }
  }, [browserTimezone, customRange?.start?.getTime?.(), customRange?.end?.getTime?.()]);

  // Prefill the custom pickers from the currently displayed range when opening 'Custom'
  const prevValueRef = React.useRef<DatePeriod>(value);
  React.useEffect(() => {
    const prev = prevValueRef.current;
    // When transitioning into 'custom', seed the inputs from the last shown range
    if (value === "custom" && prev !== "custom") {
      const src = dateRange ?? customRange; // prefer displayed preset range; fallback to last custom
      if (src?.start && src?.end) {
        setStart(getStartOfStoreDay(src.start, browserTimezone));
        setEnd(getEndOfStoreDay(src.end, browserTimezone));
      }
    }
    prevValueRef.current = value;
  }, [
    browserTimezone,
    value,
    dateRange?.start?.getTime?.(),
    dateRange?.end?.getTime?.(),
    customRange?.start?.getTime?.(),
    customRange?.end?.getTime?.(),
  ]);

  // Keep the title on the last applied range while a custom range is being edited.
  // The parent updates dateRange after Apply is clicked.
  const effectiveRange = dateRange;
  const rangeSpansMultipleWeeks = Boolean(
    effectiveRange &&
    (Date.parse(`${formatStoreDateKey(effectiveRange.end, browserTimezone)}T00:00:00.000Z`) -
      Date.parse(`${formatStoreDateKey(effectiveRange.start, browserTimezone)}T00:00:00.000Z`)) /
      (7 * 24 * 60 * 60 * 1000) >=
      1,
  );
  const showTrendAnchorToggle =
    rangeSpansMultipleWeeks &&
    (value === "last_4_weeks" || value === "custom") &&
    (trendPeriod === "month_before" || trendPeriod === "year_before");
  const shouldKeepTrendMenuOpen = (nextTrendPeriod: TrendPeriod) =>
    rangeSpansMultipleWeeks &&
    (value === "last_4_weeks" || value === "custom") &&
    (nextTrendPeriod === "month_before" || nextTrendPeriod === "year_before");
  const showRangeYears = Boolean(
    effectiveRange &&
    shouldShowStoreDateRangeComparisonYears(effectiveRange, trendComparisonRange, browserTimezone),
  );

  const renderPeriodRange = (range: DateRange) => {
    const locale = i18n.resolvedLanguage || i18n.language || undefined;
    const displayRange = formatStoreDateRangeForComparison(
      range.start,
      range.end,
      browserTimezone,
      locale,
      showRangeYears,
    );
    const sameDay =
      formatStoreDateKey(range.start, browserTimezone) ===
      formatStoreDateKey(range.end, browserTimezone);

    if (sameDay) {
      return <span>{capitalizeFirstCharacter(displayRange.startDate)}</span>;
    }

    return (
      <>
        <span>{capitalizeFirstCharacter(displayRange.startDate)}</span>
        <span aria-hidden="true" style={{ fontWeight: 400 }}>
          {" – "}
        </span>
        <span>{capitalizeFirstCharacter(displayRange.endDate)}</span>
      </>
    );
  };

  const applyCustom = () => {
    const s = getStartOfStoreDay(start, browserTimezone);
    const e = getEndOfStoreDay(end, browserTimezone);
    if (onCustomRangeChange) onCustomRangeChange({ start: s, end: e });
    // Do not trigger onChange here; parent already set value='custom' when the tab was selected.
  };

  const selectedPeriodLabel = periodOptions.find((option) => option.value === value)?.label;

  const renderPrintPeriodSummary = () => {
    if (!showRangeText || !effectiveRange) {
      return null;
    }

    return (
      <Box
        className="date-period-print-summary"
        sx={{
          alignItems: "flex-start",
          display: "none",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "text.secondary",
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            lineHeight: 1.25,
            textTransform: "uppercase",
          }}
        >
          {selectedPeriodLabel
            ? t("common.filterMenu.summaryEyebrow", {
                label: t("common.datePeriodToggle.text.currentPeriod"),
                selectedOption: selectedPeriodLabel,
              })
            : t("common.datePeriodToggle.text.currentPeriod")}
        </Typography>
        <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.35 }}>
          {renderPeriodRange(effectiveRange)}
        </Typography>
      </Box>
    );
  };

  const selectedTrendPeriodLabel = trendOptions.find(
    (option) => option.value === trendPeriod,
  )?.label;

  const renderPrintComparisonSummary = () => {
    if (!showRangeText || !trendComparisonRange) {
      return null;
    }

    return (
      <Box
        className="date-period-print-comparison-summary"
        sx={{
          alignItems: "flex-start",
          display: "none",
          flexDirection: "column",
          gap: 0.25,
        }}
      >
        <Typography
          component="span"
          sx={{
            color: "text.secondary",
            fontSize: "0.6875rem",
            fontWeight: 700,
            letterSpacing: "0.08em",
            lineHeight: 1.25,
            textTransform: "uppercase",
          }}
        >
          {selectedTrendPeriodLabel
            ? t("common.filterMenu.summaryEyebrow", {
                label: t("common.datePeriodToggle.text.selectTrendComparisonWindow"),
                selectedOption: selectedTrendPeriodLabel,
              })
            : t("common.datePeriodToggle.text.selectTrendComparisonWindow")}
        </Typography>
        <Typography component="span" sx={{ fontSize: "1rem", fontWeight: 700, lineHeight: 1.35 }}>
          {renderPeriodRange(trendComparisonRange)}
        </Typography>
      </Box>
    );
  };

  return (
    <Box
      className={className}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0,
        width: "100%",
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "flex-start" },
          justifyContent: { md: "space-between" },
          gap: { xs: 2, md: 1.5 },
          width: "100%",
        }}
      >
        {areaFilter && (
          <Box
            sx={{
              flexShrink: 0,
              width: { xs: "100%", md: "auto" },
            }}
          >
            {areaFilter}
          </Box>
        )}

        {showDeviceFilter && (
          <Box
            sx={{
              flexShrink: 0,
              marginInlineEnd: { md: areaFilter ? "auto" : undefined },
              width: { xs: "100%", md: "auto" },
            }}
          >
            <DeviceTypeFilter
              selectedDeviceType={selectedDeviceType}
              onDeviceTypeChange={onDeviceTypeChange ?? (() => undefined)}
              disabled={deviceFilterDisabled}
              summary
              includeAllOption={includeAllDeviceOption}
              className="reporting-window-device-filter"
            />
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flex: { md: "0 1 auto" },
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            gap: { xs: 2, md: 1.5 },
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flex: { md: "0 0 auto" },
              flexDirection: "column",
              gap: 0.25,
              maxWidth: { md: "450px" },
              minWidth: 0,
              width: { xs: "100%", md: "fit-content" },
            }}
          >
            <Box className="date-period-controls">
              <FilterMenuButton
                selectedValue={value}
                options={periodOptions}
                onChange={onChange}
                icon={<TodayIcon />}
                showSelectedOptionIcon
                fitContent
                controlPaddingY={1.875}
                shouldCloseOnChange={(nextPeriod) => nextPeriod !== "custom"}
                menuTitle={t("common.datePeriodToggle.text.currentPeriod")}
                minWidth={{ xs: "100%", md: "260px" }}
                maxWidth={{ md: "450px" }}
                menuMinWidth={360}
                summary={{
                  eyebrow: t("common.datePeriodToggle.text.currentPeriod"),
                  primaryText: selectedPeriodLabel,
                  supportingText:
                    showRangeText && effectiveRange ? renderPeriodRange(effectiveRange) : undefined,
                }}
                renderMenuFooter={(closeMenu) =>
                  enableCustom && value === "custom" ? (
                    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={dateLocale}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1.5}>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <DatePicker
                              label={t("common.datePeriodToggle.fields.startDate")}
                              value={start}
                              onChange={(date) => date && setStart(date)}
                              slots={{ day: RangePreviewDay }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: customDateFieldSx,
                                },
                              }}
                            />
                          </Box>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <DatePicker
                              label={t("common.datePeriodToggle.fields.endDate")}
                              value={end}
                              minDate={start}
                              onChange={(date) => date && setEnd(date)}
                              slots={{ day: RangePreviewDay }}
                              slotProps={{
                                textField: {
                                  size: "small",
                                  sx: customDateFieldSx,
                                },
                              }}
                            />
                          </Box>
                        </Stack>
                        <Button
                          fullWidth
                          variant="outlined"
                          color="primary"
                          size="small"
                          onClick={() => {
                            applyCustom();
                            closeMenu();
                          }}
                          disabled={!start || !end || end < start}
                          sx={customApplyButtonSx}
                        >
                          {t("common.apply")}
                        </Button>
                      </Stack>
                    </LocalizationProvider>
                  ) : undefined
                }
                showMenuFooterDivider={false}
              />
            </Box>

            {renderPrintPeriodSummary()}
            {renderPrintComparisonSummary()}
          </Box>

          {showTrendSelector && onTrendPeriodChange && (
            <FilterMenuButton
              selectedValue={trendPeriod}
              options={trendOptions}
              onChange={onTrendPeriodChange}
              shouldCloseOnChange={(nextTrendPeriod) => !shouldKeepTrendMenuOpen(nextTrendPeriod)}
              icon={<TrendingUpIcon />}
              showSelectedOptionIcon
              fitContent
              controlPaddingY={1.875}
              menuTitle={t("common.datePeriodToggle.text.selectTrendComparisonWindow")}
              minWidth={{ xs: "100%", md: "260px" }}
              maxWidth={{ md: "450px" }}
              menuMinWidth={320}
              summary={{
                eyebrow: t("common.datePeriodToggle.text.selectTrendComparisonWindow"),
                primaryText: selectedTrendPeriodLabel,
                supportingText:
                  showRangeText && trendComparisonRange
                    ? renderPeriodRange(trendComparisonRange)
                    : undefined,
              }}
              menuFooter={
                showTrendAnchorToggle ? (
                  <Box>
                    <Typography sx={{ fontSize: "0.8125rem", fontWeight: 600, mb: 1 }}>
                      {t("common.datePeriodToggle.text.anchor")}
                    </Typography>
                    <ToggleButtonGroup
                      exclusive
                      fullWidth
                      onChange={(_, nextAnchor: "date" | "weekday" | null) => {
                        if (nextAnchor) {
                          onAnchorTrendComparisonToWeekdaysChange?.(nextAnchor === "weekday");
                        }
                      }}
                      size="small"
                      value={anchorTrendComparisonToWeekdays ? "weekday" : "date"}
                    >
                      <ToggleButton value="date">
                        {t("common.datePeriodToggle.text.date")}
                      </ToggleButton>
                      <ToggleButton value="weekday">
                        {t("common.datePeriodToggle.text.weekday")}
                      </ToggleButton>
                    </ToggleButtonGroup>
                    <Typography
                      color="text.secondary"
                      sx={{ fontSize: "0.75rem", lineHeight: 1.4, mx: 0.5, mt: 1 }}
                    >
                      {t("common.datePeriodToggle.text.anchorComparisonToWeekdaysDescription")}
                    </Typography>
                  </Box>
                ) : undefined
              }
            />
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default ReportingWindowSelector;
